import {
  Button,
  SettingsFormControl,
  SettingsGroup,
  Switch,
  TextInputField,
  Flex
} from '@sequencehq/core-components'

import { GreyGrey30, GreyWhite } from '@sequencehq/core-components'
import { useNavigate } from 'react-router-dom'
import { useNotificationPolicySettings } from './useNotificationPolicySettings'
import { SettingsPageLayout } from '../SettingsPageLayout'
import {
  getNotificationTypesForChannel,
  hasSelectedAllNotificationTypes,
  NotificationPolicy,
  NotificationPolicyValidFormData
} from './nofitications.utils'
import { BASE_INPUT_FIELD_STYLES } from 'SettingsV2/settings.constants'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export const NotificationPolicySettings = (props: {
  data?: NotificationPolicy | null
  isLoading?: boolean
  isError?: boolean
  header: string
  onSubmit: (formData: NotificationPolicyValidFormData) => void
  isPending?: boolean
  disableNameField?: boolean
  channel: 'WEBHOOK' | 'EMAIL'
}) => {
  const { useAutomationsDunning: dunningEnabled } = useFeatureFlags()

  const { fields, onSubmit, queries, getSwitchFieldProps } =
    useNotificationPolicySettings(props.channel, dunningEnabled, props.data)

  const navigate = useNavigate()

  const isAllSelected = hasSelectedAllNotificationTypes(
    queries.formData.notificationTypes,
    props.channel,
    dunningEnabled
  )

  const isNotificationTypesEmpty =
    queries.formData.notificationTypes.length === 0

  const isEmailChannel = props.channel === 'EMAIL'
  const isWebhookChannel = props.channel === 'WEBHOOK'

  // Dunning webhook notification types are gated behind the dunning flag.
  const showDunningWebhookTypes = isWebhookChannel && dunningEnabled

  const backUrl = isEmailChannel
    ? '/settings/notifications'
    : '/settings/webhooks'

  // Notifications created with the catch-all types, cannot be modified.
  const isNotificationTypesDisabled = Boolean(
    props.data && props.data.notificationTypes === undefined
  )

  return (
    <>
      <SettingsPageLayout
        header={props.header}
        isError={props.isError}
        backButton={{
          href: backUrl,
          label: isEmailChannel ? 'Notifications' : 'Webhooks'
        }}
      >
        <SettingsGroup
          isLoading={props.isLoading}
          footer={`Separate multiple ${isEmailChannel ? 'email recipients' : 'webhook URLs'} using commas.`}
          rows={2}
        >
          <SettingsFormControl
            label="Notification name"
            htmlFor="notification-name"
          >
            <TextInputField
              {...BASE_INPUT_FIELD_STYLES}
              placeholder="My custom notification"
              id="notification-name"
              data-testid="settings.notificationPolicy.nameField"
              value={fields.name.value}
              disabled={props.disableNameField || props.isPending}
              isDisabled={props.disableNameField}
              validationErrors={fields.name.validationErrors}
              autoFocus={!props.disableNameField}
              onChange={e => {
                if (props.disableNameField) return
                fields.name.onChange(e.target.value)
              }}
            />
          </SettingsFormControl>

          {isEmailChannel && (
            <SettingsFormControl label="Recipients" htmlFor="recipients">
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                type="email"
                placeholder="hello@example.com"
                id="recipients"
                disabled={props.isPending}
                validationErrors={fields.recipients.validationErrors}
                value={fields.recipients.value}
                onChange={e =>
                  fields.recipients.onChange(e.target.value.split(','))
                }
              />
            </SettingsFormControl>
          )}

          {props.channel === 'WEBHOOK' && (
            <SettingsFormControl label="Webhook URLs" htmlFor="recipients">
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="URL"
                id="recipients"
                disabled={props.isPending}
                validationErrors={fields.recipients.validationErrors}
                value={fields.recipients.value}
                onChange={e =>
                  fields.recipients.onChange(e.target.value.split(','))
                }
              />
            </SettingsFormControl>
          )}
        </SettingsGroup>

        <SettingsGroup
          title="Notification types"
          isLoading={props.isLoading}
          footer={
            isNotificationTypesDisabled
              ? 'Notification types cannot be modified in this policy. To apply your custom configuration, create a new policy.'
              : undefined
          }
        >
          <SettingsFormControl
            label="Subscribe to all notification types"
            htmlFor="subscribe-to-all-notification-types"
          >
            <Switch
              size="md"
              as="div"
              isDisabled={isNotificationTypesDisabled}
              id="subscribe-to-all-notification-types"
              isChecked={isAllSelected}
              onChange={(value: boolean) => {
                fields.notificationTypes.onChange(
                  value
                    ? getNotificationTypesForChannel(
                        props.channel,
                        dunningEnabled
                      )
                    : []
                )
              }}
            />
          </SettingsFormControl>
        </SettingsGroup>

        {!isAllSelected && (
          <>
            <SettingsGroup
              title="Billing schedules"
              isLoading={props.isLoading}
              rows={3}
            >
              <SettingsFormControl
                label="Created"
                htmlFor="created-billing-schedule"
              >
                <Switch
                  {...getSwitchFieldProps('BILLING_SCHEDULE_CREATED')}
                  size="md"
                  as="div"
                  id="created-billing-schedule"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl
                label="Updated"
                htmlFor="updated-billing-schedule"
              >
                <Switch
                  {...getSwitchFieldProps('BILLING_SCHEDULE_UPDATED')}
                  size="md"
                  as="div"
                  id="updated-billing-schedule"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl
                label="Archive"
                htmlFor="archived-billing-schedule"
              >
                <Switch
                  {...getSwitchFieldProps('BILLING_SCHEDULE_ARCHIVED')}
                  size="md"
                  as="div"
                  id="archived-billing-schedule"
                  disabled={props.isPending}
                />
              </SettingsFormControl>
            </SettingsGroup>

            <SettingsGroup
              title="Invoices"
              isLoading={props.isLoading}
              rows={showDunningWebhookTypes ? 4 : 3}
            >
              <SettingsFormControl label="Created" htmlFor="created-invoices">
                <Switch
                  {...getSwitchFieldProps('INVOICE_CREATED')}
                  size="md"
                  as="div"
                  id="created-invoices"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl label="Updated" htmlFor="updated-invoices">
                <Switch
                  {...getSwitchFieldProps('INVOICE_UPDATED')}
                  size="md"
                  as="div"
                  id="updated-invoices"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl label="Issued" htmlFor="issued-invoices">
                <Switch
                  {...getSwitchFieldProps('INVOICE_ISSUED')}
                  size="md"
                  as="div"
                  id="issued-invoices"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              {showDunningWebhookTypes && (
                <SettingsFormControl
                  label="Reminder sent"
                  htmlFor="reminder-sent-invoices"
                >
                  <Switch
                    {...getSwitchFieldProps('INVOICE_REMINDER_SENT')}
                    size="md"
                    as="div"
                    id="reminder-sent-invoices"
                    disabled={props.isPending}
                  />
                </SettingsFormControl>
              )}
            </SettingsGroup>

            <SettingsGroup
              title="Credit notes"
              isLoading={props.isLoading}
              rows={2}
            >
              <SettingsFormControl
                label="Created"
                htmlFor="created-credit-notes"
              >
                <Switch
                  {...getSwitchFieldProps('CREDIT_NOTE_CREATED')}
                  size="md"
                  as="div"
                  id="created-credit-notes"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl
                label="Updated"
                htmlFor="updated-credit-notes"
              >
                <Switch
                  {...getSwitchFieldProps('CREDIT_NOTE_UPDATED')}
                  size="md"
                  as="div"
                  id="updated-credit-notes"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl label="Issued" htmlFor="issued-credit-notes">
                <Switch
                  {...getSwitchFieldProps('CREDIT_NOTE_ISSUED')}
                  size="md"
                  as="div"
                  id="issued-credit-notes"
                  disabled={props.isPending}
                />
              </SettingsFormControl>
            </SettingsGroup>

            <SettingsGroup title="Quotes">
              <SettingsFormControl label="Published" htmlFor="published-quotes">
                <Switch
                  {...getSwitchFieldProps('QUOTE_PUBLISHED')}
                  size="md"
                  as="div"
                  id="published-quotes"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl label="Accepted" htmlFor="accepted-quotes">
                <Switch
                  {...getSwitchFieldProps('QUOTE_ACCEPTED')}
                  size="md"
                  as="div"
                  id="accepted-quotes"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl label="Signed" htmlFor="signed-quotes">
                <Switch
                  {...getSwitchFieldProps('QUOTE_SIGNED')}
                  size="md"
                  as="div"
                  id="signed-quotes"
                  disabled={props.isPending}
                />
              </SettingsFormControl>
            </SettingsGroup>

            <SettingsGroup title="Customers">
              <SettingsFormControl label="Created" htmlFor="created-customers">
                <Switch
                  {...getSwitchFieldProps('CUSTOMER_CREATED')}
                  size="md"
                  as="div"
                  id="created-customers"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl label="Updated" htmlFor="updated-customers">
                <Switch
                  {...getSwitchFieldProps('CUSTOMER_UPDATED')}
                  size="md"
                  as="div"
                  id="updated-customers"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl label="Archive" htmlFor="archived-customers">
                <Switch
                  {...getSwitchFieldProps('CUSTOMER_ARCHIVED')}
                  size="md"
                  as="div"
                  id="archived-customers"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl
                label="Alias created"
                htmlFor="created-customer-aliases"
              >
                <Switch
                  {...getSwitchFieldProps('CUSTOMER_ALIAS_CREATED')}
                  size="md"
                  as="div"
                  id="created-customer-aliases"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl
                label="Alias updated"
                htmlFor="updated-customer-aliases"
              >
                <Switch
                  {...getSwitchFieldProps('CUSTOMER_ALIAS_UPDATED')}
                  size="md"
                  as="div"
                  id="updated-customer-aliases"
                  disabled={props.isPending}
                />
              </SettingsFormControl>

              <SettingsFormControl
                label="Alias deleted"
                htmlFor="deleted-customer-aliases"
              >
                <Switch
                  {...getSwitchFieldProps('CUSTOMER_ALIAS_DELETED')}
                  size="md"
                  as="div"
                  id="deleted-customer-aliases"
                  disabled={props.isPending}
                />
              </SettingsFormControl>
            </SettingsGroup>

            <SettingsGroup title="Merchant details">
              <SettingsFormControl label="Updated" htmlFor="updated-merchant">
                <Switch
                  {...getSwitchFieldProps('MERCHANT_UPDATED')}
                  size="md"
                  as="div"
                  id="updated-merchant"
                  disabled={props.isPending}
                />
              </SettingsFormControl>
            </SettingsGroup>

            {showDunningWebhookTypes && (
              <SettingsGroup title="Watchtower">
                <SettingsFormControl
                  label="Task assigned"
                  htmlFor="task-assigned-watchtower"
                >
                  <Switch
                    {...getSwitchFieldProps('WATCHTOWER_TASK_ASSIGNED')}
                    size="md"
                    as="div"
                    id="task-assigned-watchtower"
                    disabled={props.isPending}
                  />
                </SettingsFormControl>
              </SettingsGroup>
            )}
          </>
        )}
      </SettingsPageLayout>
      <Flex
        justifyContent="end"
        gap="8px"
        px="12px"
        py="8px"
        backgroundColor={GreyWhite}
        borderTop={`1px solid ${GreyGrey30}`}
        position="sticky"
        bottom={0}
      >
        <Button variant="secondary" onClick={() => navigate(backUrl)}>
          Cancel
        </Button>

        <Button
          variant="primary"
          disabled={
            props.isPending ||
            !queries.isDirty ||
            !queries.isValid ||
            isNotificationTypesEmpty
          }
          onClick={() => {
            onSubmit(props.onSubmit)
          }}
        >
          Save Policy
        </Button>
      </Flex>
    </>
  )
}
